<template>
  <div>
    <Section
      heading="Search Results"
      :description="searchResultsCount"
      :mode="mode"
    />
    <div class="grid gap-3 sm:gap-2">
      <div
        v-for="(item, key) in data"
        :key="key"
        class="rounded-xl border p-4"
        :class="[
          mode === 'dark'
            ? 'border-dark-border bg-dark-sidebar'
            : 'border-border-10 bg-white-100',
        ]"
      >
        <div class="flex items-start gap-3">
          <AppCheckbox
            :mode="mode"
            :id="`select-${item._source.contact.contact_id}`"
            :value="item._source.contact.contact_id"
            v-model="selectedContactIds"
          />
          <img
            v-if="item._source.member.logo_url"
            :src="item._source.member.logo_url"
            :alt="`${item._source.member.company_name || ''} logo`"
            class="h-[60px] w-[100px] flex-shrink-0 object-contain"
            loading="lazy"
          />
          <div class="min-w-0 flex-1">
            <RouterLink
              :to="{
                name: 'member-details',
                params: { id: item._source.member.member_id },
              }"
              class="block text-[16px] font-semibold hover:text-primary-100 hover:underline"
            >
              {{ item._source.member.company_name || "—" }}
            </RouterLink>
            <RouterLink
              :to="{
                name: 'contact-details',
                params: { id: item._source.contact.contact_id },
              }"
              class="block text-[14px] font-medium hover:text-primary-100 hover:underline"
            >
              {{ contactDisplayName(item._source.contact) }}
            </RouterLink>
            <div
              class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[14px] sm:grid-cols-1"
            >
              <a
                v-if="item._source.contact.email"
                :href="`mailto:${item._source.contact.email}`"
                class="truncate font-medium text-primary-100 underline"
                >{{ item._source.contact.email }}</a
              >
              <a
                v-if="normalizeLink(item._source.contact.linkedin_url)"
                :href="normalizeLink(item._source.contact.linkedin_url)"
                target="_blank"
                rel="noopener noreferrer"
                class="truncate font-medium text-primary-100 underline"
                >LinkedIn</a
              >
              <span
                v-if="phoneFor(item._source)"
                :class="[
                  mode === 'dark' ? 'text-dark-text' : 'text-black-80',
                ]"
                >{{ phoneFor(item._source) }}</span
              >
              <a
                v-if="normalizeLink(item._source.member.url)"
                :href="normalizeLink(item._source.member.url)"
                target="_blank"
                rel="noopener noreferrer"
                class="truncate font-medium text-primary-100 underline"
                >{{ item._source.member.url }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-[48px] sm:mt-[32px]">
      <AppCard
        :mode="mode"
        title="Compose your Message"
        subtitle="Here is the list of members based on your search filters. <br class='sm:hidden' />You can select companies below to send them an email."
      >
        <template #body>
          <Notes :list="notesList" />
          <div v-if="data.length >= 500" class="mt-4 text-red-600 font-semibold text-sm">
            Maximum search results exceeded, sending to first 500
          </div>
          <div class="mt-6 sm:mt-4">
            <div class="mb-6 grid grid-cols-2 gap-3 sm:mb-4 sm:grid-cols-1">
              <div
                class="rounded-lg bg-opacity-50 p-4 text-center"
                :class="[mode === 'dark' ? 'bg-dark-border' : 'bg-black-10']"
              >
                <span
                  class="mb-1 block text-[14px] font-medium leading-none"
                  :class="[
                    mode === 'dark' ? 'text-dark-text' : 'text-black-80',
                  ]"
                  >Contact</span
                >
                <span class="text-[14px] font-semibold leading-none"
                  >{{ contact.first_name }} {{ contact.last_name }}</span
                >
              </div>
              <div
                class="rounded-lg bg-opacity-50 p-4 text-center"
                :class="[mode === 'dark' ? 'bg-dark-border' : 'bg-black-10']"
              >
                <span
                  class="mb-1 block text-[14px] font-medium leading-none"
                  :class="[
                    mode === 'dark' ? 'text-dark-text' : 'text-black-80',
                  ]"
                  >Company</span
                >
                <span class="text-[14px] font-semibold leading-none">{{
                  member.company_name
                }}</span>
              </div>
            </div>
            <div class="grid gap-4">
              <AppInput
                :mode="mode"
                id="subject"
                title="Subject"
                placeholder="Enter a subject for your message"
                v-model="subject"
              />
              <div>
                <AppLabel :mode="mode" htmlFor="message" title="Message" />
                <div :class="[mode === 'dark' ? 'dark-editor' : '']">
                  <v-md-editor
                    v-model="message"
                    right-toolbar="preview sync-scroll fullscreen"
                    height="400px"
                  ></v-md-editor>
                </div>
              </div>
            </div>
            <div class="mt-6 sm:mt-4">
              <AppButton
                title="Send"
                @action="send"
                :isDisabled="!subject || !message"
              />
            </div>
          </div>
        </template>
      </AppCard>
    </div>
  </div>
</template>

<script>
import Section from "@C/Common/Section.vue";
import Notes from "@C/Common/Notes.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppLabel from "@C/Form/AppLabel.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "SearchResults",
  props: {
    data: {
      type: Array,
      default: [],
    },
    contact: {
      type: Object,
      default: null,
    },
    member: {
      type: Object,
      default: null,
    },
    mode: String,
    countries: {
      type: Array,
      default: [],
    },
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  components: {
    Section,
    Notes,
    AppCheckbox,
    AppCard,
    AppLabel,
    AppInput,
    AppButton,
  },
  data() {
    return {
      message: "",
      subject: "",
      selectedContactIds: [],
      notesList: [
        "You may NOT use this feature to solicit employees, buy/sell product or disparage anyone.",
      ],
    };
  },
  mounted() {
    this.selectedContactIds = this.data.map(
      (item) => item._source.contact.contact_id,
    );
  },
  methods: {
    contactDisplayName(contact) {
      const first = (contact && contact.first_name) || "";
      const last = (contact && contact.last_name) || "";
      const joined = `${first} ${last}`.trim();
      return joined || "—";
    },
    phoneFor(source) {
      // Show the contact's work phone; if blank/null, fall back to the
      // company's primary phone.
      const work = source && source.contact && source.contact.work_phone;
      if (work && String(work).trim()) return work;
      const company = source && source.member && source.member.company_phone1;
      return company && String(company).trim() ? company : "";
    },
    normalizeLink(url) {
      // Returns a usable href, or "" when the stored value doesn't look like
      // a real URL (e.g., placeholder text like "LinkedIn URL"). Callers should
      // check the result in their v-if so nothing renders when invalid.
      if (!url) return "";
      const s = String(url).trim();
      if (!s) return "";
      // Already a real scheme? trust it.
      if (/^[a-z][a-z0-9+.\-]*:\/\//i.test(s) || /^mailto:/i.test(s)) return s;
      // No-scheme heuristic: must look like a host (no whitespace, has a dot,
      // and contains only legal URL characters).
      if (/\s/.test(s)) return "";
      if (!/\./.test(s)) return "";
      if (!/^[A-Za-z0-9._~:/?#\[\]@!$&'()*+,;=%-]+$/.test(s)) return "";
      return `https://${s}`;
    },
    async send() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        recipient_list: this.selectedContactIds.join(","),
        subject: this.subject,
        body: this.message,
      };
      try {
        const { data } = await ApiRequest().post(`/send_mail`, payload);
        if (data) {
          this.toastStore.show("Mail queued successfully!", "success");
          this.subject = "";
          this.message = "";
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
  computed: {
    searchResultsCount() {
      if (this.data.length >= 500) {
        return "Showing first 500 only, please narrow your search";
      } else {
        return `Showing ${this.data.length} matches`;
      }
    },
  },
};
</script>
